import React,{useEffect,useRef,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  fetchDisputeEvidence,
  uploadDisputeEvidence
} from '../../services/deliveryConfirmationService';

const {FiExternalLink,FiImage,FiLoader,FiUpload}=FiIcons;

const DisputeEvidencePanel=({
  disputeId,
  orderId,
  canUpload=true
})=>{
  const [evidence,setEvidence]=useState([]);
  const [loading,setLoading]=useState(true);
  const [uploading,setUploading]=useState(false);
  const [error,setError]=useState('');
  const inputRef=useRef(null);

  useEffect(()=>{
    let active=true;

    fetchDisputeEvidence(disputeId)
      .then((items)=>{
        if (active) setEvidence(items);
      })
      .catch((requestError)=>{
        if (active) setError(requestError.message || 'Unable to load evidence.');
      })
      .finally(()=>{
        if (active) setLoading(false);
      });

    return ()=> {
      active=false;
    };
  },[disputeId]);

  const handleUpload=async(event)=>{
    const files=Array.from(event.target.files || []);
    event.target.value='';

    if (!files.length) return;

    setUploading(true);
    setError('');

    try {
      const uploaded=await uploadDisputeEvidence({
        disputeId,
        orderId,
        files
      });
      setEvidence((current)=>[...current,...uploaded]);
    } catch (uploadError) {
      setError(uploadError.message || 'Unable to upload evidence.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center text-[10px] font-black uppercase tracking-widest text-slate-500">
          <SafeIcon icon={FiImage} className="mr-2 text-indigo-500" />
          Evidence
        </p>

        {canUpload && (
          <button
            type="button"
            onClick={()=>inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center rounded-xl bg-indigo-50 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-indigo-600 disabled:opacity-50"
          >
            {uploading
              ? <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
              : <SafeIcon icon={FiUpload} className="mr-2" />}
            Upload images
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      {error && (
        <p className="mt-3 rounded-xl bg-red-50 p-3 text-xs font-bold text-red-700">
          {error}
        </p>
      )}

      {loading ? (
        <SafeIcon icon={FiLoader} className="mt-4 animate-spin text-indigo-500" />
      ) : evidence.length ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {evidence.map((item)=>(
            <a
              key={item.id}
              href={item.file_url}
              target="_blank"
              rel="noreferrer"
              className="group overflow-hidden rounded-xl border border-slate-100 bg-slate-50"
            >
              <img
                src={item.file_url}
                alt={item.file_name}
                className="h-28 w-full object-cover transition-transform group-hover:scale-105"
              />
              <span className="flex items-center justify-between gap-2 p-2 text-[9px] font-bold text-slate-500">
                <span className="truncate">{item.file_name}</span>
                <SafeIcon icon={FiExternalLink} />
              </span>
            </a>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs font-medium text-slate-400">
          No evidence uploaded.
        </p>
      )}
    </div>
  );
};

export default DisputeEvidencePanel;